import { useState, useEffect } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { copilotService } from '../services/copilotService';
import { CopilotMessage } from '../../../types';
import { useSocketEvent, SOCKET_EVENTS } from '../../../lib/websocket';

/**
 * Private staff↔AI copilot side-conversation for a conversation.
 *
 * Mirrors useMessages but for the staff-only copilot stream: fetches history,
 * appends socket-delivered turns (dedup by id), and exposes a send mutation.
 * Both the staff prompt and the AI reply arrive over `copilot:message`, so the
 * mutation result is deduped against what the socket already appended.
 */
export function useCopilotMessages(conversationId: string | null) {
  const [messages, setMessages] = useState<CopilotMessage[]>([]);

  const { data: fetched, isLoading } = useQuery({
    queryKey: ['copilot-messages', conversationId],
    queryFn: () => copilotService.getMessages(conversationId!),
    enabled: !!conversationId,
  });

  useEffect(() => {
    if (fetched) setMessages(fetched);
  }, [fetched]);

  useSocketEvent(SOCKET_EVENTS.COPILOT_MESSAGE, (data) => {
    if (data.conversationId !== conversationId) return;
    const incoming = copilotService.mapCopilotMessage(
      data.message as unknown as Record<string, any>,
    );
    setMessages((prev) => {
      if (prev.some((m) => m.id === incoming.id)) return prev;
      return [...prev, incoming];
    });
  });

  const sendMutation = useMutation({
    mutationFn: (content: string) => copilotService.sendMessage(conversationId!, content),
    onSuccess: (message) => {
      setMessages((prev) => {
        if (prev.some((m) => m.id === message.id)) return prev;
        return [...prev, message];
      });
    },
  });

  return {
    messages,
    isLoading,
    sendMessage: sendMutation.mutate,
    isSending: sendMutation.isPending,
  };
}
